import type {
  MmkSortOrder,
  QuizQuestion,
  MmkFilterCategory,
  MmkViewMode,
} from "../types/mmk";

export type AnsweredDetail = { selectedOptionIndex: number; correct: boolean };

const MMK_QUIZ_STATE_KEY = "mmk_quiz_state_";
const MMK_FOCUS_SESSION_KEY = "mmk_focus_session_";
const MMK_SORT_ORDER_KEY = "mmk_sort_order_";
const MMK_UI_STATE_KEY = "mmk_ui_state_";

function getQuizStateKey(scopeKey: string): string {
  return scopeKey ? `${MMK_QUIZ_STATE_KEY}${scopeKey}` : "";
}

function getFocusSessionStateKey(scopeKey: string): string {
  return scopeKey ? `${MMK_FOCUS_SESSION_KEY}${scopeKey}` : "";
}

function getSortOrderKey(scopeKey: string): string {
  return scopeKey ? `${MMK_SORT_ORDER_KEY}${scopeKey}` : "";
}

function getUiStateKey(scopeKey: string): string {
  return scopeKey ? `${MMK_UI_STATE_KEY}${scopeKey}` : "";
}

export function loadSortOrder(scopeKey: string): MmkSortOrder | null {
  const key = getSortOrderKey(scopeKey);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (raw !== "oldest_first" && raw !== "newest_first") return null;
    return raw;
  } catch {
    return null;
  }
}

export function saveSortOrder(scopeKey: string, order: MmkSortOrder): void {
  const key = getSortOrderKey(scopeKey);
  if (!key) return;
  try {
    localStorage.setItem(key, order);
  } catch {
    // ignore
  }
}

export interface MmkUiState {
  activeCategory: MmkFilterCategory;
  searchTerm: string;
  viewMode: MmkViewMode;
  isSelectionMode: boolean;
}

export function loadMmkUiState(scopeKey: string): MmkUiState | null {
  const key = getUiStateKey(scopeKey);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MmkUiState> | null;
    if (!parsed || typeof parsed !== "object") return null;
    const activeCategory =
      parsed.activeCategory && typeof parsed.activeCategory === "string"
        ? (parsed.activeCategory as MmkFilterCategory)
        : ("All" as MmkFilterCategory);
    const searchTerm =
      typeof parsed.searchTerm === "string" ? parsed.searchTerm : "";
    const viewMode =
      parsed.viewMode === "scroll" || parsed.viewMode === "grid"
        ? parsed.viewMode
        : ("scroll" as MmkViewMode);
    const isSelectionMode =
      typeof parsed.isSelectionMode === "boolean"
        ? parsed.isSelectionMode
        : false;
    return { activeCategory, searchTerm, viewMode, isSelectionMode };
  } catch {
    return null;
  }
}

export function saveMmkUiState(
  scopeKey: string,
  state: MmkUiState,
): void {
  const key = getUiStateKey(scopeKey);
  if (!key) return;
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        activeCategory: state.activeCategory,
        searchTerm: state.searchTerm,
        viewMode: state.viewMode,
        isSelectionMode: state.isSelectionMode,
      }),
    );
  } catch {
    // ignore
  }
}

function parseMkkNumber(s: string): number {
  const match = (s || "").match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

export function sortMmkItems<T extends { mkk_number: string }>(
  items: T[],
  order: MmkSortOrder,
): T[] {
  const arr = [...items];
  arr.sort((a, b) => {
    const numA = parseMkkNumber(a.mkk_number);
    const numB = parseMkkNumber(b.mkk_number);
    if (order === "oldest_first") return numA - numB;
    return numB - numA;
  });
  return arr;
}

export interface QuizPersistedState {
  questions: QuizQuestion[];
  index: number;
  correctIds: string[];
  wrongIds: string[];
  answeredDetails: Record<string, AnsweredDetail>;
}

export function loadQuizState(scopeKey: string): QuizPersistedState | null {
  const key = getQuizStateKey(scopeKey);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      questions?: unknown;
      index?: unknown;
      correctIds?: unknown;
      wrongIds?: unknown;
      answeredDetails?: Record<string, { selectedOptionIndex: number; correct: boolean }>;
    };
    if (
      !parsed ||
      !Array.isArray(parsed.questions) ||
      parsed.questions.length === 0
    ) {
      return null;
    }
    const questions = parsed.questions as QuizQuestion[];
    const questionItemIds = new Set(
      questions.map((q: QuizQuestion) => q?.item_id).filter(Boolean),
    );
    const answeredDetails: Record<string, AnsweredDetail> = {};
    if (parsed.answeredDetails && typeof parsed.answeredDetails === "object") {
      for (const [k, v] of Object.entries(parsed.answeredDetails)) {
        const itemId = k.split("::")[0];
        if (
          questionItemIds.has(itemId) &&
          v &&
          typeof v === "object" &&
          typeof (v as AnsweredDetail).selectedOptionIndex === "number" &&
          typeof (v as AnsweredDetail).correct === "boolean"
        ) {
          answeredDetails[k] = v as AnsweredDetail;
        }
      }
    }
    return {
      questions,
      index: Math.min(
        Math.max(0, Number(parsed.index) || 0),
        parsed.questions.length - 1,
      ),
      correctIds: Array.isArray(parsed.correctIds)
        ? parsed.correctIds.filter((x: unknown) => typeof x === "string")
        : [],
      wrongIds: Array.isArray(parsed.wrongIds)
        ? parsed.wrongIds.filter((x: unknown) => typeof x === "string")
        : [],
      answeredDetails,
    };
  } catch {
    return null;
  }
}

export function saveQuizState(
  scopeKey: string,
  state: QuizPersistedState,
): void {
  const key = getQuizStateKey(scopeKey);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function clearQuizState(scopeKey: string): void {
  const key = getQuizStateKey(scopeKey);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export interface FocusSessionPersistedState {
  sessionItemIds: string[];
  sessionTotal: number;
  index: number;
  flipped: boolean;
}

export function loadFocusSessionState(
  scopeKey: string,
): FocusSessionPersistedState | null {
  const key = getFocusSessionStateKey(scopeKey);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      sessionItemIds?: unknown;
      sessionTotal?: unknown;
      index?: unknown;
      flipped?: unknown;
    };
    if (!parsed || !Array.isArray(parsed.sessionItemIds)) return null;
    const ids = parsed.sessionItemIds.filter(
      (x: unknown): x is string => typeof x === "string",
    );
    if (ids.length === 0) return null;
    const parsedTotal = Number(parsed.sessionTotal);
    const sessionTotal =
      Number.isFinite(parsedTotal) && parsedTotal >= ids.length
        ? Math.floor(parsedTotal)
        : ids.length;
    return {
      sessionItemIds: ids,
      sessionTotal,
      index: Math.min(
        Math.max(0, Number(parsed.index) || 0),
        ids.length - 1,
      ),
      flipped: Boolean(parsed.flipped),
    };
  } catch {
    return null;
  }
}

export function saveFocusSessionState(
  scopeKey: string,
  state: FocusSessionPersistedState,
): void {
  const key = getFocusSessionStateKey(scopeKey);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function clearFocusSessionState(scopeKey: string): void {
  const key = getFocusSessionStateKey(scopeKey);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}
